/**
 * Подготовка персоны к динамичным сценам (§22, §71).
 *
 * Две короткие записи вместо двадцати снимков: в первой человек поворачивает
 * голову, во второй поворачивается сам. Кадры извлекает система.
 *
 * Команды идут по времени — и звучат вслух. На первой живой съёмке
 * выяснилось, что читать текст и одновременно поворачивать голову
 * невозможно, а на записи в полный рост человек стоит в двух метрах
 * от экрана и не видит вообще ничего. Поэтому команда короткая, лежит
 * поверх видоискателя (а не сбоку, куда приходится отводить взгляд)
 * и произносится голосом.
 *
 * Экран отдельный, вне общего каркаса — как и основная запись (§71): во время
 * съёмки на экране не должно быть ничего, кроме видоискателя и команды.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { dynamicPrepTakes, prepCueAt, prepSecondsToNext, type PrepTake } from '@alterium/shared';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { useRecorder } from '../../features/capture/useRecorder';
import { useSpokenCue } from '../../features/capture/useSpokenCue';
import { uploadFile } from '../../features/upload/uploadFile';
import { personaApi } from '../../api/persona';

/** Сколько ждём разбор, прежде чем считать, что он ничего не дал. */
const GRACE_MS = 30_000;

const formatTime = (seconds: number): string => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function DynamicPrepPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorder = useRecorder();

  /** Какую из двух записей снимаем сейчас. */
  const [index, setIndex] = useState(0);
  /** Уже загруженные записи: id ассетов по порядку дублей. */
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  /** Команды вслух. Выключаемо: человек может снимать там, где нельзя шуметь. */
  const [sound, setSound] = useState(true);
  /**
   * Человек вернулся переснять уже готовую подготовку.
   *
   * Причины бывают разные: сменилась внешность, свет был неудачный, или
   * просто хочется попробовать ещё раз. Экран «всё готово» без выхода
   * на пересъёмку — тупик: другого пути к этой съёмке в приложении нет.
   */
  const [redo, setRedo] = useState(false);
  /** Записи отправлены — ждём исхода разбора. */
  const [submitted, setSubmitted] = useState(false);
  /**
   * Выдержка после отправки прошла.
   *
   * Сразу после отправки задача ещё не видна в очереди, и без выдержки мы бы
   * объявили о неудаче, не начав.
   */
  const [graceOver, setGraceOver] = useState(false);

  const take = dynamicPrepTakes[index] as PrepTake;

  const status = useQuery({
    queryKey: ['dynamic-prep'],
    queryFn: personaApi.dynamicPrep,
    /**
     * Пока идёт разбор — состояние меняется само.
     *
     * И пока мы ждём его исхода после отправки: задача может закончиться
     * ничем, и узнать об этом можно только переспросив. Без этого экран
     * оставался на «разбираем» навсегда.
     */
    refetchInterval: (query) =>
      query.state.data?.processing || (submitted && !query.state.data?.ready) ? 5000 : false,
  });

  // Живой поток в видоискатель. Отдельным эффектом: поток появляется асинхронно.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;

    const stream = recorder.stream.current;
    if (stream && recorder.state !== 'recorded') {
      element.srcObject = stream;
      return;
    }
    element.srcObject = null;
  }, [recorder.state, recorder.stream]);

  // Выдержка после отправки: до неё «не готово» ещё ничего не значит.
  useEffect(() => {
    if (!submitted) return;

    const timer = setTimeout(() => setGraceOver(true), GRACE_MS);
    return () => clearTimeout(timer);
  }, [submitted]);

  // Автостоп: команды кончились, дальше снимать нечего.
  useEffect(() => {
    if (recorder.state === 'recording' && recorder.elapsedSec >= take.durationSec) recorder.stop();
  }, [recorder, recorder.state, recorder.elapsedSec, take.durationSec]);

  const upload = useMutation({
    mutationFn: async () => {
      const captured = recorder.captured;
      if (!captured) throw new Error(t('consent.noRecording'));

      const extension = captured.mimeType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([captured.blob], `persona-${take.id}.${extension}`, {
        type: captured.mimeType.split(';')[0] ?? 'video/webm',
      });

      const asset = await uploadFile(file, {
        kind: 'video',
        onProgress: ({ loaded, total }) => setUploadProgress(Math.round((loaded / total) * 100)),
      });

      const next = [...assetIds, asset.id];
      setAssetIds(next);

      // Отправляем обе записи разом: разбор смотрит на них вместе, и половина
      // набора — это не половина результата, а его отсутствие.
      if (next.length === dynamicPrepTakes.length) {
        await personaApi.startDynamicPrep(next[0] as string, next[1] as string);
        setSubmitted(true);
      }
      return next;
    },
    onSuccess: (next) => {
      recorder.reset();
      if (next.length < dynamicPrepTakes.length) setIndex(next.length);
      else void status.refetch();
    },
    onSettled: () => setUploadProgress(null),
  });

  const cueNow = prepCueAt(take, recorder.elapsedSec);

  /**
   * Произносим короткую команду, а не длинное пояснение: вслух нужна
   * та же пара слов, что и на экране, иначе человек дослушивает фразу,
   * когда поворачиваться уже поздно.
   */
  useSpokenCue(recorder.state === 'recording' ? t(`prep.short.${cueNow.key}`) : '', {
    enabled: sound,
    locale: i18n.language,
  });

  const done = assetIds.length === dynamicPrepTakes.length;
  const ready = status.data?.ready === true;
  const cue = cueNow;
  const cueIndex = take.cues.indexOf(cue);
  const nextCue = take.cues[cueIndex + 1];
  const secondsToNext = prepSecondsToNext(take, recorder.elapsedSec);

  // Готово — если только человек не пришёл переснимать.
  if (ready && !redo && !recorder.captured) {
    return (
      <Shell onClose={() => navigate('/persona')}>
        <div className="flex flex-col gap-4">
          <h1 className="text-section-title font-semibold">{t('prep.ready')}</h1>
          <p className="text-body text-content-secondary">
            {t('prep.readyHint', { count: status.data?.frames ?? 0 })}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" onClick={() => navigate('/persona')}>
              {t('prep.toPersona')}
            </Button>
            {/* Пересъёмка заменит набор целиком — прежние кадры не смешиваются
                с новыми (см. обработчик разбора). */}
            <Button
              onClick={() => {
                setRedo(true);
                setAssetIds([]);
                setIndex(0);
                setSubmitted(false);
                setGraceOver(false);
              }}
            >
              {t('prep.again')}
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  /**
   * Разбор закончился ничем.
   *
   * Пока этого состояния не было, экран висел на «разбираем записи» вечно:
   * задача давно завершилась, кадров ноль, а человек ждал. Ждать нечего —
   * надо сказать об этом и дать переснять.
   *
   * Отсчёт от отправки: сразу после неё задача ещё не видна в очереди,
   * и без выдержки мы бы объявили о неудаче, не начав.
   */
  const failed = done && submitted && graceOver && !status.data?.processing && !ready;

  if (failed) {
    return (
      <Shell onClose={() => navigate('/persona')}>
        <div className="flex flex-col gap-4">
          <h1 className="text-section-title font-semibold">{t('prep.failed')}</h1>
          <p className="text-body text-content-secondary">{t('prep.failedHint')}</p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setAssetIds([]);
                setIndex(0);
                setSubmitted(false);
                setGraceOver(false);
                setRedo(true);
              }}
            >
              {t('prep.again')}
            </Button>
            <Button onClick={() => navigate('/persona')}>{t('prep.toPersona')}</Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell onClose={() => navigate('/persona')}>
        <div className="flex flex-col gap-4">
          <h1 className="text-section-title font-semibold">{t('prep.processing')}</h1>
          <p className="text-body text-content-secondary">{t('prep.processingHint')}</p>
          <div className="progress-track">
            <div className="progress-bar w-1/3 animate-pulse" />
          </div>
          <div>
            <Button onClick={() => navigate('/persona')}>{t('prep.toPersona')}</Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <div className="flex min-h-full flex-col bg-page">
      {recorder.state !== 'recording' ? (
        <header className="flex items-center justify-between px-5 py-4">
          <span className="text-card-title font-semibold tracking-tight">
            {t(index === 0 ? 'prep.take1' : 'prep.take2')}
          </span>
          <button
            type="button"
            onClick={() => {
              recorder.release();
              navigate('/persona');
            }}
            aria-label={t('common.close')}
            className="rounded-control p-1.5 text-content-muted transition-colors duration-fast hover:text-content"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </header>
      ) : null}

      <div className="flex flex-1 flex-col gap-4 px-5 pb-8 lg:flex-row lg:gap-8 lg:px-8">
        <div className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-card bg-surface-secondary lg:mx-0">
          <div className="aspect-[9/16] w-full">
            {/* Разные `key`: иначе React переиспользует элемент с живым
                потоком и вместо записи снова показывает камеру. */}
            {recorder.state === 'recorded' && recorder.captured ? (
              <video
                key="recorded"
                src={recorder.captured.url}
                controls
                playsInline
                className="h-full w-full object-cover"
              />
            ) : (
              <video
                key="live"
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="h-full w-full -scale-x-100 object-cover"
              />
            )}
          </div>

          {recorder.state === 'recording' ? (
            <>
              <div className="absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-control bg-page/80 px-3 py-1.5">
                <span className="h-2 w-2 rounded-full bg-error" />
                <span className="text-meta tabular-nums text-content">
                  {formatTime(recorder.elapsedSec)}
                </span>
                <span className="text-meta text-content-muted">
                  / {formatTime(take.durationSec)}
                </span>
              </div>

              {/*
                Команда поверх кадра, очень крупно.

                Сбоку от видоискателя её приходилось искать глазами, а с двух
                метров — не видно вовсе. Здесь она там же, куда человек и так
                смотрит, и читается издалека одним взглядом.
              */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 bg-gradient-to-t from-page/90 to-transparent px-4 pb-8 pt-16 text-center">
                <p className="text-[30px] font-semibold leading-tight text-content drop-shadow-lg sm:text-[36px]">
                  {t(`prep.short.${cue.key}`)}
                </p>
                {secondsToNext > 0 && nextCue ? (
                  <p className="text-[15px] text-content-secondary">
                    {t(`prep.short.${nextCue.key}`)} · {t('prep.nextIn', { value: secondsToNext })}
                  </p>
                ) : null}
              </div>

              <div className="absolute inset-x-0 bottom-0">
                <div className="progress-track rounded-none">
                  <div
                    className="progress-bar rounded-none"
                    style={{
                      width: `${Math.min(100, (recorder.elapsedSec / take.durationSec) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-4">
          {recorder.error ? <Alert tone="error">{recorder.error}</Alert> : null}
          {upload.isError ? <Alert tone="error">{(upload.error as Error).message}</Alert> : null}

          {recorder.state === 'idle' || recorder.state === 'error' ? (
            <div className="flex flex-col gap-5 pt-2">
              <div>
                <h1 className="text-section-title font-semibold">{t('prep.title')}</h1>
                <p className="mt-2 text-body text-content-secondary">{t('prep.subtitle')}</p>
              </div>
              <p className="text-body text-content-secondary">{t('prep.why')}</p>
              <p className="text-body text-content-secondary">
                {t(index === 1 ? 'prep.standPhone' : 'prep.take1hint')}
              </p>
              <div>
                <Button variant="primary" onClick={() => void recorder.requestPermission()}>
                  {recorder.state === 'error' ? t('common.tryAgain') : t('capture.enableCamera')}
                </Button>
              </div>
              {recorder.state === 'error' ? (
                <p className="text-meta text-content-muted">{t('capture.permissionHint')}</p>
              ) : null}
            </div>
          ) : null}

          {recorder.state === 'ready' ? (
            <div className="flex flex-col gap-3">
              <p className="text-body text-content-secondary">
                {t(index === 1 ? 'prep.standPhone' : 'prep.take1hint')}
              </p>

              {/* Звук можно выключить: снимать иногда приходится там,
                  где нельзя шуметь. Тогда остаётся крупная надпись. */}
              <label className="flex items-center gap-2 text-body text-content-secondary">
                <input
                  type="checkbox"
                  checked={sound}
                  onChange={(event) => setSound(event.target.checked)}
                  className="h-4 w-4 accent-accent"
                />
                {t('prep.sound')}
              </label>
              <p className="text-meta text-content-muted">{t('prep.soundHint')}</p>

              <div className="flex justify-center">
                <Button variant="primary" onClick={() => void recorder.start()}>
                  {t('capture.start')}
                </Button>
              </div>
            </div>
          ) : null}

          {recorder.state === 'recording' ? (
            <div className="flex justify-center pt-4">
              <Button
                variant="secondary"
                onClick={recorder.stop}
                disabled={recorder.elapsedSec < take.minSec}
              >
                {t('capture.stop')}
              </Button>
            </div>
          ) : null}

          {recorder.state === 'recorded' && recorder.captured ? (
            <div className="flex flex-col gap-4 pt-2">
              <div>
                <p className="text-card-title">{t('prep.recorded')}</p>
                <p className="mt-1 text-body text-content-secondary">
                  {t('capture.duration', { value: formatTime(recorder.captured.durationSec) })}
                </p>
              </div>

              {uploadProgress !== null ? (
                <div className="progress-track">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={() => upload.mutate()}
                  disabled={upload.isPending}
                >
                  {upload.isPending
                    ? t('prep.sending')
                    : index === 0
                      ? t('prep.next')
                      : t('prep.send')}
                </Button>
                <Button onClick={recorder.reset} disabled={upload.isPending}>
                  {t('prep.rerecord')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Простой каркас для состояний без камеры: разбор и готовность. */
function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-full flex-col bg-page">
      <header className="flex items-center justify-between px-5 py-4">
        <span className="text-card-title font-semibold tracking-tight">{t('prep.title')}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="rounded-control p-1.5 text-content-muted transition-colors duration-fast hover:text-content"
        >
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </header>
      <div className="mx-auto w-full max-w-lg px-5 py-8">{children}</div>
    </div>
  );
}
